import React,{
	useState,
	useEffect
} from 'react'
import {
	useDispatch,
	useSelector,
} from 'react-redux'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import DatePicker from "react-datepicker"
import Button from 'react-bootstrap/Button'
import Select from 'react-select'
import {
	setShow,
	getOneRepMaxes,
	addNewMaxMetric,
} from '../../../state/percentMaxCalculator'
import {useEvent} from '../../../hooks/events'
import {
	autoFillExerciseSets,
	appendExerciseWeight,
	replaceExerciseWeight,
	extractExerciseFromRoutine
} from '../../../state/events'
import { dateToYMD } from '../../../util/util'

const mapMetricsToOptions = metrics => metrics.map(m=>({
	label:`${m.value} (${m.assessment_name})`,
	value:m.value
}))

/**
 * If a number is an int return it. Else fix to 2 places
 */
const intFloatFix = n => Number.isInteger(n) ? n : n.toFixed(2)

const PercentMaxCalculator = ()=>{

	/* --- hooks --- */
	const dispatch = useDispatch()
	const {
		show,
		workout_id,
		exercise_id,
		integratedMetrics,
	} = useSelector( state => state.percentMaxCalculator )

	const workout = useEvent('workout',workout_id)
	const exercise = workout && extractExerciseFromRoutine(workout.routine,'exercise',exercise_id)
	console.log(exercise)

	const client_id = useSelector(state=>state.allEvents.workouts.entities[workout_id]?.client_id)
	const client = useSelector(state=>state.users.clients.entities[client_id])
	/* --- Local state --- */
	//new metric state
	const [name,setName] = useState('')
	const [description,setDescription] = useState('')
	const [initialValue,setInitialValue] = useState(false)
	const [initialDate,setInitialDate] = useState(new Date)
	const [hideNewMetricForm,setHideNewMetricForm] = useState(true)

	const [setsToUpdate,setSetsToUpdate] = useState([])

	//calculated value state
	const [percent,setPercent] = useState(null)
	const [maxVal,setMaxVal] = useState(null)
	const percentVal = intFloatFix(percent*maxVal)

	/* --- Handlers --- */
	const handleClose = () => {
		dispatch(setShow(false))
	}

	const handleAdd = (addToAllClients)=>{
		dispatch(addNewMaxMetric({
			initialDate:dateToYMD(initialDate),
			addToAllClients,
			initialValue,
			description,
			name,
		}))
	}

	const handleToggleSet = (id)=>{
		if(setsToUpdate.includes(id)){
			setSetsToUpdate(setsToUpdate.filter(compareId=>compareId!==id))
		}else{
			setSetsToUpdate([...setsToUpdate,id])
		}
	}


	const handleToggleAllSets = ()=>{
		if(setsToUpdate.length < exercise.set_data.length){
			setSetsToUpdate(exercise.set_data.map(s=>s.id))
		}else{
			setSetsToUpdate([])
		}
	}

	const handleAddToAll = ()=>{handleAdd(true)}
	const handleAddToSingle = ()=>{handleAdd(false)}

	const handlePercentChange = e => {
		const newPercent = parseFloat(e.target.value)
		if(isNaN(newPercent)) setPercent(0)
		else setPercent(newPercent/100)
	}

	const handleMaxValSelect = opt => {
		setMaxVal(opt.value)
	}

	const handleReplace = () => {
		//update weight
		dispatch(autoFillExerciseSets({
			update:{assigned_weight:percentVal},
			ids:setsToUpdate,
			type:'exercise',
			exercise_id,
			workout_id,
		}))
		setSetsToUpdate([])
		//close modal
		handleClose()
	}

	/* --- effects --- */

	//whenver workout_id changes get new percent max
	//we only really need to do this everytime a new
	//client is selected (this way is simpler).
	useEffect(()=>{
		dispatch(getOneRepMaxes())
	},[dispatch,workout_id])

	return (
		<Modal show={show} onHide={handleClose}>
			<Modal.Header closeButton>
				<strong>Percent Max Calculator</strong>
			</Modal.Header>
			<Modal.Body>
				<Button 
					onClick={()=>{setHideNewMetricForm(!hideNewMetricForm)}}
					variant="link" 
					size="sm"
				>
					Add New Max Metric
				</Button>
				<div style={{
					height:hideNewMetricForm?'0':'auto',
					overflow:'hidden'
				}}>
					<Form.Control 
						onChange={e=>{setName(e.target.value)}}
						placeholder="Metric (eg Squat Max)" 
						className="mb-1" 
						size="sm"
					/>
					<Form.Control 
						onChange={e=>{setDescription(e.target.value)}}
						placeholder="description" 
						as="textarea" 
						size="sm"
					/>
					<small>Optional initial data for {client?.user_name}</small>
					<div className="d-flex mb-1">
						<Form.Control 
							onChange={e=>{setInitialValue(e.target.value)}}
							placeholder="Initial value" 
							className="mr-1"
							size="sm"
						/>
						<DatePicker 
							onChange={newDate=>{setInitialDate(newDate)}}
							selected={initialDate}
							customInput={<Form.Control size="sm"/>} 
						/>
					</div>
					<Button 
						variant="outline-primary" 
						onClick={handleAddToAll} 
						size="sm" 
						block
					>
						Add to All Clients
					</Button>
					<Button 
						onClick={handleAddToSingle} 
						variant="primary" 
						size="sm" 
						block
					>
						Add to Just this Client
					</Button>
					<div>
						You can also add/view one rep max metrics/progress on the 
						<a className="mx-1" href={`/assementGraphsTrainers?clientId=${client_id}`}>
							metrics
						</a> 
						page.
					</div>
				</div>
				<hr/>
				<div className="d-flex justify-content-between align-items-center">
					<Form.Control 
						onChange={handlePercentChange} 
						className="text-center"
						style={{width:'70px'}}
						placeholder="%RM"
					/>
					<span>% of</span>
					<div style={{width:'180px'}}>
						<Select 
							onChange={handleMaxValSelect}
							options={mapMetricsToOptions(integratedMetrics)}
						/>
					</div>
					=
					<span className="text-center" style={{width:'100px'}}>{percentVal}</span>
				</div>
				<small>
					Don't see the metric you want? make sure you've added some data 
					<a className="m-0 mx-1" target="_blank" href={`/assementGraphsTrainers?clientId=${client_id}`}>
						here
					</a> 
					or add a new max metric with an initial value.
				</small>
				<div>
					<small>Select sets to update</small>
					<hr/>
					<small>
						<Form.Check 
							checked={setsToUpdate.length === exercise?.set_data?.length}
							onChange={handleToggleAllSets}
							id={`percent-rm-all`}
							label="All sets" 
						/>
					</small>
					{exercise?.set_data?.map((set,i)=>(
						<small className="d-block">
							<Form.Check 
								onChange={()=>{handleToggleSet(set.id)}}
								checked={setsToUpdate.includes(set.id)}
								id={`percent-rm-${i}`}
								label={`Set ${i+1}: ${set.assigned.weight}${setsToUpdate.includes(set.id)?` -> ${percentVal}`:''}`}
							/>
							
						</small>
					))}
				</div>
				<div className="d-flex justify-content-end mt-1">
					<Button onClick={handleReplace} variant="outline-danger" size="sm">
						Replace Weight(s)
					</Button>
				</div>
			</Modal.Body>
		</Modal>
	)
}

export default PercentMaxCalculator